import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useFilterSamplingProcedure from "@app/hooks/useFilterSamplingProcedure";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { toExponentialUppercase } from "@app/utils/functions";
import { useEquipment, useUser } from "@services/servicesGenericsTables";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

const Measurement = () => {
  const { watch, setValue, getValues } = useFormContext();
  const { currentMeasurement } = useAnalysis();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const filterSamplingProcedureList = useFilterSamplingProcedure("AAFE");

  const masa1 = watch("mass_glass_without_sample") || 0;
  const masa2 = watch("mass_glass_with_sample") || 0;
  const concentracion = watch("carrier_concentration") || 0;
  const volumen = watch("carrier_volume_added_sample") || 0;

  const volumenAA = watch("atomic_absorption_measurement") || 0;
  const volumenMatraz = watch("dissolution_flask_volume") || 0;
  const masaTotal = watch("mass_sample") || 0;
  const alicuotaS = watch("aliquot_s") || 0;

  useEffect(() => {
    if (masa1 !== undefined && masa2 !== undefined) {
      const totalMass = (parseFloat(masa2) - parseFloat(masa1)).toFixed(4);
      const totalMassFormatted = totalMass.replace(",", ".");
      setValue("mass_sample", totalMassFormatted);
    } else {
      setValue("mass_sample", "0.00");
    }
  }, [masa1, masa2]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (concentracion !== undefined && volumen !== undefined) {
      const calculatedValue = (
        (parseFloat(concentracion) * parseFloat(volumen)) /
        1000
      ).toFixed(4);
      const calculatedValueFormatted = calculatedValue.replace(",", ".");

      setValue("carrier_mass", calculatedValueFormatted);
    }
  }, [concentracion, volumen, setValue]);

  useEffect(() => {
    if (
      volumenAA !== undefined &&
      volumenMatraz !== undefined &&
      masaTotal !== undefined &&
      alicuotaS !== undefined &&
      concentracion !== undefined &&
      volumen !== undefined
    ) {
      const rtoAA =
        (parseFloat(volumenAA) *
          parseFloat(volumenMatraz) *
          parseFloat(masaTotal)) /
        (parseFloat(alicuotaS) *
          parseFloat(concentracion) *
          parseFloat(volumen));

      if (isNaN(rtoAA)) {
        setValue("counting_by_one", 0);
      } else {
        const rtoAAFormatted = rtoAA.toFixed(4).replace(",", ".");
        setValue("counting_by_one", rtoAAFormatted);
      }
    } else {
      setValue("counting_by_one", 0);
    }
  }, [volumenAA, volumenMatraz, masaTotal, alicuotaS, concentracion, volumen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (masaTotal !== undefined && alicuotaS !== undefined) {
      const analysisAmount = parseFloat(getValues("analysis_amount_sample"));
      const cantidad =
        (analysisAmount * (parseFloat(masaTotal) - parseFloat(alicuotaS))) /
        parseFloat(masaTotal);

      if (isNaN(cantidad)) {
        setValue("vial_sample_quantity", "0");
      } else {
        const cantidadCientifico = toExponentialUppercase(cantidad, 3);
        setValue("vial_sample_quantity", cantidadCientifico);
      }
    } else {
      setValue("vial_sample_quantity", "0.000000e+0");
    }
  }, [masaTotal, alicuotaS, setValue, getValues]);
  return (
    <>
      {currentMeasurement.index !== undefined ? (
        <React.Fragment key={currentMeasurement.id}>
          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="measurement_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentMeasurement.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(
                getValues("measurement_processing_procedure")
              )}
              name="measurement_processing_procedure"
              options={filterSamplingProcedureList}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              disabled={!currentMeasurement.canEdit}
            />
            <MDatePicker
              name="measurement_date_time"
              pickerType="date"
              label="Fecha Inicio"
              disabled={!currentMeasurement.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("measurement_equipment"))}
              name="measurement_equipment"
              options={equipmentList}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              multiple={true}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="mass_glass_without_sample"
              label="Masa del vaso sin muestra (g)"
              placeholder="Masa del vaso sin muestra (g)"
              type="number"
              validation={{ required: "La masa es requerida" }}
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="mass_glass_with_sample"
              label="Masa del vaso con muestra (g)"
              placeholder="Masa del vaso con muestra (g)"
              type="number"
              /*               validation={{ required: "La masa es requerida" }}   */
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="mass_sample"
              label="Masa de la muestra “s” (g)"
              placeholder="Masa de la muestra “s” (g)"
              type="text"
              fullWidth
              disabled
            />
            <MTextField
              name="aliquot_s"
              label="b (g) (Alícuota de s)"
              placeholder="b (g) (Alícuota de s)"
              type="number"
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="carrier_volume_added_sample"
              label="Volumen del portador añadido a muestra “Vp” (ml)"
              placeholder="Volumen del portador añadido a muestra “Vp” (ml)"
              type="number"
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              key={JSON.stringify(watch("carrier_concentration"))}
              name="carrier_concentration"
              label="Concentración del portador de Fe “cp” (mg/l)"
              placeholder="Concentración del portador de Fe “cp” (mg/l)"
              type="number"
              fullWidth
              step={0.01}
              disabled
            />
            <MTextField
              name="carrier_mass"
              label="Masa del portador de Fe “Np” (mg)"
              placeholder="Masa del portador de Fe “Np” (mg)"
              type="text"
              fullWidth
              disabled
            />
            <MTextField
              name="dissolution_flask_volume"
              label="Vol. matraz disolución (ml)"
              placeholder="Vol. matraz disolución (ml)"
              type="number"
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="atomic_absorption_measurement"
              label="Medida AA (mg/l)"
              placeholder="Medida AA (mg/l)"
              type="number"
              /* validation={{ required: "El volumen es requerido" }} */
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="counting_by_one"
              label="Rto. en tanto por uno"
              placeholder="Rto. en tanto por uno"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              name="vial_sample_quantity"
              label="Cantidad de muestra vial"
              placeholder="Cantidad de muestra vial"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              name="valid_measure"
              label="Medida Válida"
              placeholder="Medida Válida"
              type="text"
              disabled
              fullWidth
              step={0.01}
            />
          </ResponsiveFormContainer>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default Measurement;
